import { create } from 'zustand';
import type { SubscriptionTier, ServiceLevel } from '@/types';
import type {
  SubscriptionPlan,
  PlanFeatures,
  PlanLimits,
  UsageMetrics,
  SubscriptionStatus,
  BillingInfo,
  BillingInterval,
  FeatureName,
  LimitName,
  FeatureCheckResult,
  LimitCheckResult,
  PaymentMethod,
  Invoice,
} from '@/types/subscription';

// ============================================================================
// PLAN DEFINITIONS
// ============================================================================

const PLAN_LIMITS: Record<SubscriptionTier, PlanLimits> = {
  free: {
    maxUnits: 5,
    maxUsers: 2,
    maxWorkOrdersPerMonth: 10,
    maxDevices: 0,
    maxProperties: 1,
    storageGB: 1,
  },
  starter: {
    maxUnits: 25,
    maxUsers: 5,
    maxWorkOrdersPerMonth: null, // unlimited
    maxDevices: 10,
    maxProperties: 3,
    storageGB: 10,
  },
  professional: {
    maxUnits: 100,
    maxUsers: 15,
    maxWorkOrdersPerMonth: null,
    maxDevices: 50,
    maxProperties: 10,
    storageGB: 50,
  },
  enterprise: {
    maxUnits: null, // unlimited
    maxUsers: null,
    maxWorkOrdersPerMonth: null,
    maxDevices: null,
    maxProperties: null,
    storageGB: 500,
  },
};

const PLAN_FEATURES: Record<SubscriptionTier, PlanFeatures> = {
  free: {
    // Core
    basicReporting: true,
    workOrders: true,
    vendorPortal: false,
    assetTracking: false,
    // Analytics
    basicAnalytics: true,
    advancedAnalytics: false,
    customReports: false,
    // IoT
    iotMonitoring: false,
    smartLocks: false,
    leakSensors: false,
    thermostats: false,
    smokeDetectors: false,
    motionSensors: false,
    autoAlertReports: false,
    // Disruptor Features
    qrCodeWorkflows: false,
    magicLinkVendorPortal: false,
    floorPlanMapping: false,
    // Integrations
    emailIntegration: true,
    smsNotifications: false,
    apiAccess: false,
    webhooks: false,
    customIntegrations: false,
    // Enterprise
    sso: false,
    auditLog: false,
    whiteLabel: false,
    dedicatedSupport: false,
    slaGuarantee: false,
  },
  starter: {
    // Core
    basicReporting: true,
    workOrders: true,
    vendorPortal: true,
    assetTracking: true,
    // Analytics
    basicAnalytics: true,
    advancedAnalytics: false,
    customReports: false,
    // IoT
    iotMonitoring: true,
    smartLocks: false,
    leakSensors: true,
    thermostats: true,
    smokeDetectors: true,
    motionSensors: false,
    autoAlertReports: true,
    // Disruptor Features
    qrCodeWorkflows: true,        // QR codes available in Starter+
    magicLinkVendorPortal: false, // Magic links require Professional+
    floorPlanMapping: false,      // Floor plans require Professional+
    // Integrations
    emailIntegration: true,
    smsNotifications: true,
    apiAccess: false,
    webhooks: false,
    customIntegrations: false,
    // Enterprise
    sso: false,
    auditLog: false,
    whiteLabel: false,
    dedicatedSupport: false,
    slaGuarantee: false,
  },
  professional: {
    // Core
    basicReporting: true,
    workOrders: true,
    vendorPortal: true,
    assetTracking: true,
    // Analytics
    basicAnalytics: true,
    advancedAnalytics: true,
    customReports: true,
    // IoT
    iotMonitoring: true,
    smartLocks: true,
    leakSensors: true,
    thermostats: true,
    smokeDetectors: true,
    motionSensors: true,
    autoAlertReports: true,
    // Disruptor Features
    qrCodeWorkflows: true,
    magicLinkVendorPortal: true,
    floorPlanMapping: true,
    // Integrations
    emailIntegration: true,
    smsNotifications: true,
    apiAccess: false,
    webhooks: true,
    customIntegrations: false,
    // Enterprise
    sso: false,
    auditLog: true,
    whiteLabel: false,
    dedicatedSupport: false,
    slaGuarantee: false,
  },
  enterprise: {
    // Core
    basicReporting: true,
    workOrders: true,
    vendorPortal: true,
    assetTracking: true,
    // Analytics
    basicAnalytics: true,
    advancedAnalytics: true,
    customReports: true,
    // IoT
    iotMonitoring: true,
    smartLocks: true,
    leakSensors: true,
    thermostats: true,
    smokeDetectors: true,
    motionSensors: true,
    autoAlertReports: true,
    // Disruptor Features
    qrCodeWorkflows: true,
    magicLinkVendorPortal: true,
    floorPlanMapping: true,
    // Integrations
    emailIntegration: true,
    smsNotifications: true,
    apiAccess: true,
    webhooks: true,
    customIntegrations: true,
    // Enterprise
    sso: true,
    auditLog: true,
    whiteLabel: true,
    dedicatedSupport: true,
    slaGuarantee: true,
  },
};

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    tier: 'free',
    name: 'Free',
    description: 'For small properties just getting started',
    pricing: {
      monthly: 0,
      annual: 0,
      perUnitMonthly: 0,
      perUnitAnnual: 0,
    },
    limits: PLAN_LIMITS.free,
    features: PLAN_FEATURES.free,
  },
  {
    tier: 'starter',
    name: 'Starter',
    description: 'For growing property portfolios',
    pricing: {
      monthly: 59,
      annual: 47, // ~20% discount
      perUnitMonthly: 2,
      perUnitAnnual: 1.6,
    },
    limits: PLAN_LIMITS.starter,
    features: PLAN_FEATURES.starter,
  },
  {
    tier: 'professional',
    name: 'Professional',
    description: 'Full-featured for serious property managers',
    pricing: {
      monthly: 179,
      annual: 143,
      perUnitMonthly: 1.5,
      perUnitAnnual: 1.2,
    },
    limits: PLAN_LIMITS.professional,
    features: PLAN_FEATURES.professional,
    popular: true,
  },
  {
    tier: 'enterprise',
    name: 'Enterprise',
    description: 'Custom solutions for large organizations',
    pricing: {
      monthly: 349,
      annual: 279,
      perUnitMonthly: 1,
      perUnitAnnual: 0.8,
    },
    limits: PLAN_LIMITS.enterprise,
    features: PLAN_FEATURES.enterprise,
  },
];

// ============================================================================
// STORE STATE & ACTIONS
// ============================================================================

interface SubscriptionStore {
  // State
  tier: SubscriptionTier;
  serviceLevel: ServiceLevel;
  status: SubscriptionStatus;
  isTrialing: boolean;
  trialEndsAt: string | null;
  billingInterval: BillingInterval;
  billing: BillingInfo | null;
  usage: UsageMetrics;
  paymentMethods: PaymentMethod[];
  invoices: Invoice[];
  isDemoMode: boolean;

  // Actions
  setTier: (tier: SubscriptionTier) => void;
  setServiceLevel: (level: ServiceLevel) => void;
  setStatus: (status: SubscriptionStatus) => void;
  setTrialInfo: (isTrialing: boolean, endsAt: string | null) => void;
  setBillingInterval: (interval: BillingInterval) => void;
  setUsage: (usage: Partial<UsageMetrics>) => void;
  incrementWorkOrders: () => void;
  setDemoMode: (isDemoMode: boolean) => void;

  // Feature & Limit Checks
  checkFeature: (feature: FeatureName) => FeatureCheckResult;
  checkLimit: (limit: LimitName, currentValue?: number) => LimitCheckResult;
  hasFeature: (feature: FeatureName) => boolean;
  isWithinLimit: (limit: LimitName, currentValue?: number) => boolean;

  // Plan Info
  getCurrentPlan: () => SubscriptionPlan;
  getPlanByTier: (tier: SubscriptionTier) => SubscriptionPlan;
  getTrialDaysRemaining: () => number;
  canUpgradeTo: (tier: SubscriptionTier) => boolean;
}

// Default usage for demo
const defaultUsage: UsageMetrics = {
  currentUnits: 48,
  currentUsers: 4,
  currentProperties: 4,
  currentDevices: 12,
  workOrdersThisMonth: 45,
  storageUsedGB: 2.3,
  billingCycleStart: new Date(new Date().setDate(1)).toISOString(),
  billingCycleEnd: new Date(new Date().setMonth(new Date().getMonth() + 1, 0)).toISOString(),
};

export const useSubscriptionStore = create<SubscriptionStore>((set, get) => ({
  // Initial state (demo mode with professional tier)
  tier: 'professional',
  serviceLevel: 'coreops_complete',
  status: 'active',
  isTrialing: false,
  trialEndsAt: null,
  billingInterval: 'monthly',
  billing: {
    interval: 'monthly',
    currentPeriodStart: defaultUsage.billingCycleStart,
    currentPeriodEnd: defaultUsage.billingCycleEnd,
    nextBillingDate: defaultUsage.billingCycleEnd,
    amount: 179,
    currency: 'USD',
  },
  usage: defaultUsage,
  paymentMethods: [
    {
      id: 'pm_demo_1',
      type: 'card',
      last4: '4242',
      brand: 'Visa',
      expiryMonth: 12,
      expiryYear: 2027,
      isDefault: true,
    },
  ],
  invoices: [
    {
      id: 'inv_demo_1',
      number: 'INV-2024-001',
      date: '2024-12-01',
      dueDate: '2024-12-15',
      amount: 179,
      status: 'paid',
      lineItems: [
        { description: 'Professional Plan - Monthly', quantity: 1, unitPrice: 179, amount: 179 },
      ],
    },
    {
      id: 'inv_demo_2',
      number: 'INV-2024-002',
      date: '2024-11-01',
      dueDate: '2024-11-15',
      amount: 179,
      status: 'paid',
      lineItems: [
        { description: 'Professional Plan - Monthly', quantity: 1, unitPrice: 179, amount: 179 },
      ],
    },
  ],
  isDemoMode: true,

  // Actions
  setTier: (tier) => set({ tier }),
  setServiceLevel: (serviceLevel) => set({ serviceLevel }),
  setStatus: (status) => set({ status }),
  setTrialInfo: (isTrialing, trialEndsAt) => set({ isTrialing, trialEndsAt }),
  setBillingInterval: (billingInterval) => set({ billingInterval }),
  setUsage: (usage) => set((state) => ({ usage: { ...state.usage, ...usage } })),
  incrementWorkOrders: () =>
    set((state) => ({
      usage: { ...state.usage, workOrdersThisMonth: state.usage.workOrdersThisMonth + 1 },
    })),
  setDemoMode: (isDemoMode) => set({ isDemoMode }),

  // Feature Check
  checkFeature: (feature) => {
    const { tier, isDemoMode } = get();

    // Demo mode bypasses restrictions
    if (isDemoMode) {
      return { allowed: true };
    }

    const features = PLAN_FEATURES[tier];
    const allowed = features[feature];

    if (allowed) {
      return { allowed: true };
    }

    // Find minimum tier that has this feature
    const tierOrder: SubscriptionTier[] = ['free', 'starter', 'professional', 'enterprise'];
    const upgradeRequired = tierOrder.find((t) => PLAN_FEATURES[t][feature]) || 'enterprise';

    return {
      allowed: false,
      reason: `This feature requires the ${upgradeRequired} plan or higher`,
      upgradeRequired,
    };
  },

  // Limit Check
  checkLimit: (limit, currentValue) => {
    const { tier, usage, isDemoMode } = get();

    // Demo mode bypasses restrictions
    if (isDemoMode) {
      return {
        allowed: true,
        current: currentValue ?? 0,
        max: null,
        remaining: null,
        percentage: 0,
      };
    }

    const limits = PLAN_LIMITS[tier];
    const max = limits[limit];

    // Get current value from usage if not provided
    const current = currentValue ?? (() => {
      switch (limit) {
        case 'maxUnits':
          return usage.currentUnits;
        case 'maxUsers':
          return usage.currentUsers;
        case 'maxWorkOrdersPerMonth':
          return usage.workOrdersThisMonth;
        case 'maxDevices':
          return usage.currentDevices;
        case 'maxProperties':
          return usage.currentProperties;
        case 'storageGB':
          return usage.storageUsedGB;
        default:
          return 0;
      }
    })();

    // Unlimited
    if (max === null) {
      return {
        allowed: true,
        current,
        max: null,
        remaining: null,
        percentage: 0,
      };
    }

    const remaining = max - current;
    const percentage = Math.min((current / max) * 100, 100);
    const allowed = current < max;

    if (allowed) {
      return {
        allowed: true,
        current,
        max,
        remaining,
        percentage,
      };
    }

    // Find minimum tier with higher limit
    const tierOrder: SubscriptionTier[] = ['free', 'starter', 'professional', 'enterprise'];
    const currentIndex = tierOrder.indexOf(tier);
    const upgradeRequired = tierOrder
      .slice(currentIndex + 1)
      .find((t) => {
        const tierMax = PLAN_LIMITS[t][limit];
        return tierMax === null || tierMax > current;
      });

    return {
      allowed: false,
      current,
      max,
      remaining: 0,
      percentage: 100,
      upgradeRequired,
    };
  },

  hasFeature: (feature) => get().checkFeature(feature).allowed,
  isWithinLimit: (limit, currentValue) => get().checkLimit(limit, currentValue).allowed,

  // Plan Info
  getCurrentPlan: () => {
    const { tier } = get();
    return SUBSCRIPTION_PLANS.find((p) => p.tier === tier) || SUBSCRIPTION_PLANS[0];
  },

  getPlanByTier: (tier) => {
    return SUBSCRIPTION_PLANS.find((p) => p.tier === tier) || SUBSCRIPTION_PLANS[0];
  },

  getTrialDaysRemaining: () => {
    const { isTrialing, trialEndsAt } = get();
    if (!isTrialing || !trialEndsAt) return 0;

    const now = new Date();
    const end = new Date(trialEndsAt);
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  },

  canUpgradeTo: (targetTier) => {
    const { tier } = get();
    const tierOrder: SubscriptionTier[] = ['free', 'starter', 'professional', 'enterprise'];
    return tierOrder.indexOf(targetTier) > tierOrder.indexOf(tier);
  },
}));

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

export const useCurrentPlan = () => useSubscriptionStore((state) => state.getCurrentPlan());
export const useSubscriptionTier = () => useSubscriptionStore((state) => state.tier);
export const useSubscriptionStatus = () => useSubscriptionStore((state) => state.status);
export const useUsageMetrics = () => useSubscriptionStore((state) => state.usage);
export const useIsTrialing = () => useSubscriptionStore((state) => state.isTrialing);
export const useIsDemoMode = () => useSubscriptionStore((state) => state.isDemoMode);

// Feature check hook
export const useFeatureCheck = (feature: FeatureName) =>
  useSubscriptionStore((state) => state.checkFeature(feature));

// Limit check hook
export const useLimitCheck = (limit: LimitName, currentValue?: number) =>
  useSubscriptionStore((state) => state.checkLimit(limit, currentValue));

export { PLAN_LIMITS, PLAN_FEATURES };
